import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const Navigation = ({ onSectionClick, activeTab, setActiveTab }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: "Individual", tab: "individual", isTab: true },
    { label: "Partner", tab: "partner", isTab: true },
    { label: "Corporate", tab: "corporate", isTab: true },
    { label: "Meet Our Team", path: "/team", isTab: false },
    { label: "Blogs", path: "/blogs", isTab: false },
    { label: "Contact Us", section: "contact", isTab: false },
  ];

  const handleNavClick = (item) => {
    if (item.isTab) {
      if (setActiveTab) setActiveTab(item.tab);   // guard against undefined
      onSectionClick("tab-switcher");
    } else if (item.path) {
      navigate(item.path);
    } else if (item.section) {
      onSectionClick(item.section);
      if (setActiveTab) setActiveTab(item.section); // Set active tab to contact to trigger color
    }
    setMobileMenuOpen(false);
  };

  // Add this helper function before the Navigation component
  const getAppStoreLink = () => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return "https://apps.apple.com/app/healthismplus/id6444691038";
    }
    return "https://play.google.com/store/apps/details?id=com.healthismplus.customer";
  };

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm"
      style={{ backgroundColor: '#FFFFFF' }}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              setActiveTab("individual");
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            data-testid="logo"
          >
            <img src="/logo_icon.svg" alt="HealthismPlus" className="h-9 w-xl" />
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7">
            {navItems.map((item) => {
              // Check if item is active based on tab state OR current URL path
              const isActive = (item.isTab && activeTab === item.tab) ||
                (item.path && location.pathname === item.path) ||
                (item.section && activeTab === item.section);

              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className={`text-sm font-medium transition-colors relative py-1 ${isActive
                    ? "text-[#13B58E]"
                    : "text-[#6B7A99] hover:text-[#13B58E]"
                    }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <motion.a
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              href={getAppStoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-full font-semibold text-white text-sm"
              style={{
                background: `linear-gradient(90deg, #006B5A 0%, #27A28B 100%)`,
              }}
              data-testid="nav-download-cta"
            >
              Download App
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              href="https://hms.healthismplus.com/#/login"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block ml-5 px-5 py-3 rounded-full bg-[#2A3077] text-white text-sm font-semibold shadow-sm hover:opacity-95 transition"
              style={{
                background: `linear-gradient(90deg, #2A3077 0%, #8FB3FF 100%)`,
              }}
            >
              Sign in to HMS
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white border-t border-gray-100"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 space-y-3">
            {navItems.map((item) => {
              const isActive = (item.isTab && activeTab === item.tab) ||
                (item.path && location.pathname === item.path) ||
                (item.section && activeTab === item.section);

              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className={`block w-full text-left py-2 font-medium text-sm ${isActive
                    ? "text-[#13B58E]"
                    : "text-[#6B7A99]"
                    }`}
                >
                  {item.label}
                </button>
              );
            })}
            <a
              href={getAppStoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-2.5 rounded-full font-semibold text-white text-center text-sm"
              style={{
                background: `linear-gradient(90deg, #006B5A 0%, #27A28B 100%)`,
              }}
            >
              Download App
            </a>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navigation;